import { Box, Card, CardContent, Grid, Typography, Button, CircularProgress, TextField, MenuItem, Select, FormControl, InputLabel, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Input } from '@mui/material';
import { Lock } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Fuel = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const cardDetails = location.state?.cardDetails;
  console.log('Card Details: ', cardDetails);

  // fallback if props not passed directly
  const inputCardNumber = '' || cardDetails?.cardNumber;
  const inputClientId = '' || cardDetails?.clientId;
  const inputCustomerId = '' || cardDetails?.customerId;
  const inputCustomerName = '' || cardDetails?.customerName;
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [fuelCardData, setFuelCardData] = useState({});
  const doNotPerformCustomerFiltration = localStorage.getItem('role') === 'Super Admin' || localStorage.getItem('role') === 'Employee';
  const [formData, setFormData] = useState({
    policyNumber: '',
    driverName: '',
    personalId: '',
    subFleet: '',
    unitNumber: '',
    xref: '',
    driverId: '',
    status: '',
    limit: '',
  });

  // Passcode protection state
  const [showPersonalId, setShowPersonalId] = useState(false);
  const [isPasscodeDialogOpen, setIsPasscodeDialogOpen] = useState(false);
  const [enteredPasscode, setEnteredPasscode] = useState('');

  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/v1/getCardInfo?cardNumber=${inputCardNumber}`);
        const data = await response.json();

        if (data) {
          const nameInfo = data.infos.find(info => info.infoId === 'NAME')?.reportValue || '';
          const ppinInfo = data.infos.find(info => info.infoId === 'PPIN')?.matchValue || '';
          const ssubInfo = data.infos.find(info => info.infoId === 'SSUB')?.reportValue || '';
          const unitInfo = data.infos.find(info => info.infoId === 'UNIT')?.matchValue || '';
          console.log('Personal ID: ', ppinInfo);

          setFuelCardData(data);
          setFormData({
            policyNumber: data.header.policyNumber,
            driverName: nameInfo,
            personalId: ppinInfo,
            subFleet: ssubInfo,
            unitNumber: unitInfo,
            xref: data.header.companyXRef || '',
            driverId: '',
            status: data.header.status || '',
            limit: '',
          });
        }
      } catch (error) {
        console.error('Error fetching fuel card details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCardDetails();
  }, [inputCardNumber, BACKEND_URL]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        cardNumber: inputCardNumber,
        status: formData.status,
        driverName: formData.driverName,
        unitNumber: formData.unitNumber,
      }).toString();

      const response = await fetch(`${BACKEND_URL}/v1/setCardInfo?${queryParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        alert('Fuel card details updated successfully!');
        navigate('/fuel-card'); // Redirect to FuelCardListView
      } else {
        const errorMessage = await response.text();
        alert(`Error updating fuel card details: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error submitting fuel card details:', error);
      alert('An error occurred while updating fuel card details.');
    } finally {
      setIsLoading(false); // Hide spinner (runs in both success and error cases)
    }
  };

  // Passcode functions
  const handleUnlockClick = () => setIsPasscodeDialogOpen(true);
  const handlePasscodeSubmit = () => {
    if (enteredPasscode === formData.personalId) {
      setShowPersonalId(true);
    } else {
      alert('Incorrect passcode. Try again!');
    }
    setIsPasscodeDialogOpen(false);
    setEnteredPasscode('');
  };

  return (
    <div>
      <Typography
        variant="h1"
        sx={{
          fontSize: '14px',
          fontWeight: '800',
          textTransform: 'uppercase',
          fontFamily: 'sans-serif',
          // marginTop: '50px',
        }}
        gutterBottom
      >
        CARD MANAGEMENT
      </Typography>
      {loading ? (
        <Grid container justifyContent="center">
          <CircularProgress color="success" />
        </Grid>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* <Card> */}
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Card Number"
                  value={inputCardNumber}
                  disabled
                  fullWidth
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '20%' }, // Reduce placeholder size
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Policy Number"
                  name="policyNumber"
                  value={formData.policyNumber}
                  disabled
                  fullWidth
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-40%' }, // Reduce placeholder size
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Driver Name"
                  name="driverName"
                  value={formData.driverName}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-40%' }, // Reduce placeholder size
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Personal ID Number"
                  name="personalId"
                  value={showPersonalId ? formData.personalId : '******'}
                  disabled
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleUnlockClick} edge="end" size="small">
                          <Lock fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                />
              </Grid>
              {doNotPerformCustomerFiltration && (
                <Grid item xs={4}>
                  <TextField
                    label="Sub Fleet Identifier"
                    name="subFleet"
                    value={formData.subFleet}
                    disabled
                    fullWidth
                    InputLabelProps={{
                      sx: { fontSize: '11px', top: '-40%' }, // Reduce placeholder size
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 24,
                        minHeight: 24,
                      },
                      '& .MuiInputBase-input': {
                        fontSize: 11,
                        padding: '2px',
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={4}>
                <TextField
                  label="Unit #"
                  name="unitNumber"
                  value={formData.unitNumber}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-40%' }, // Reduce placeholder size
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                />
              </Grid>
              {doNotPerformCustomerFiltration && (
                <Grid item xs={4}>
                  <TextField
                    label="XREF"
                    name="xref"
                    value={formData.xref}
                    disabled
                    fullWidth
                    InputLabelProps={{
                      sx: { fontSize: '11px', top: '-40%' }, // Reduce placeholder size
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 24,
                        minHeight: 24,
                      },
                      '& .MuiInputBase-input': {
                        fontSize: 11,
                        padding: '2px',
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={4}>
                <TextField
                  label="Driver ID"
                  name="driverId"
                  value={formData.driverId}
                  disabled
                  fullWidth
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-40%' }, // Reduce placeholder size
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                >
                  <InputLabel
                    sx={{
                      fontSize: '12px',
                      top: '-40%',
                    }}
                  >
                    Status
                  </InputLabel>
                  <Select label="status" name="status" value={formData.status} onChange={handleChange}>
                    <MenuItem value="ACTIVE" sx={{ fontSize: '12px' }}>
                      ACTIVE
                    </MenuItem>
                    <MenuItem value="INACTIVE" sx={{ fontSize: '12px' }}>
                      INACTIVE
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Limit"
                  name="limit"
                  value={formData.limit}
                  disabled
                  fullWidth
                  InputLabelProps={{
                    sx: { fontSize: '11px', top: '-40%' }, // Reduce placeholder size
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 24,
                      minHeight: 24,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 11,
                      padding: '2px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    // mt: -5.5,
                    // ml: 42,
                    padding: '4px 12px', // smaller padding
                    fontSize: '10px', // smaller font
                    backgroundColor: '#31478F',
                  }}
                >
                  {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Update Details'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
          {/* </Card> */}
        </form>
      )}

      {/* Passcode Dialog */}
      <Dialog open={isPasscodeDialogOpen} onClose={() => setIsPasscodeDialogOpen(false)}>
        <DialogTitle>Enter Passcode</DialogTitle>
        <DialogContent>
          <Input type="password" value={enteredPasscode} onChange={e => setEnteredPasscode(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPasscodeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePasscodeSubmit} color="primary">
            Unlock
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Fuel;
